import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Resources } from '@local/client-contracts';
import { UTextareaComponent } from '@local/ui-infra';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { isEnterKey, KeyName } from '@local/ts-infra';
import { AppService } from '@shared/services/app.service';
import { upperFirst } from 'lodash';

export interface FeedbackData {
  resourceId: string;
  linkId: string;
  searchId: string;
}

export type FeedbackPopupDisplayView = 'feedbackSelecting' | 'wrongReason' | 'wrongReasonFree' | 'wrongReasonFinal';
@Component({
  selector: 'feedback-popup',
  templateUrl: './feedback-popup.component.html',
  styleUrls: ['./feedback-popup.component.scss'],
})
export class FeedbackPopupComponent implements OnInit {
  private _displayView: FeedbackPopupDisplayView = 'feedbackSelecting';

  readonly WRONG_FEEDBACK_REASONS: string[] = [
    'Not what I was looking for',
    "Result isn't relevant to the query",
    'Result should have been higher',
    'Other',
  ];

  feedbackComment = 'Your feedback will be used to <br/> help improve the product';
  reasonSelected: number = null;
  feedbackSelected: Resources.FeedbackType;
  freeReason: string;
  freeReasonModel: string;
  private keyHandlerId: string;
  selectedIndex: number = null;

  get displayView(): FeedbackPopupDisplayView {
    return this._displayView;
  }

  set displayView(value: FeedbackPopupDisplayView) {
    this._displayView = value;
    this.displayViewStatus.emit(this.displayView);
  }

  @Input() resourceId: string;
  @Input() snippet: string;
  @Input() resourceAppId: string;
  @Input() linkId: string;
  @Input() searchId: string;
  @Input() index: number;
  @Input() query: string;
  @Input() resourceLinkUrl: string;
  @Input() resourceName: string;

  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: Event) {
    if (['wrongReason', 'wrongReasonFree'].includes(this.displayView)) this.sendFeedback();
  }

  @Output() displayViewStatus = new EventEmitter<FeedbackPopupDisplayView>();
  @Output() feedbackSent = new EventEmitter<void>();
  @Output() closePopup = new EventEmitter<void>();

  @ViewChild('textAreaReason') textAreaReason: UTextareaComponent;

  constructor(
    private cdr: ChangeDetectorRef,
    private resourcesService: ResourcesService,
    private keyboardService: KeyboardService,
    private appService: AppService
  ) {
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys: Array<KeyName>, event) => this.handleKeys(keys, event), 9);
  }

  ngOnInit() {
    this.displayViewStatus.emit(this.displayView);
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    event.stopPropagation();
    const key = keys[0];
    if (key === 'escape') {
      return this.closePopUp();
    }
    switch (this.displayView) {
      case 'feedbackSelecting':
        {
          event.preventDefault();
          if (isEnterKey(key) && this.selectedIndex != null) {
            const feedbackType: Resources.FeedbackType = this.selectedIndex === 0 ? 'Positive' : 'Negative';
            this.selectFeedback(feedbackType);
            return;
          }
          switch (key) {
            case 'ArrowRight':
              this.selectedIndex = 1;
              break;
            case 'ArrowLeft':
              this.selectedIndex = 0;
              break;
          }
        }
        break;
      case 'wrongReason': {
        event.preventDefault();
        const maxReasonIndex = this.WRONG_FEEDBACK_REASONS.length - 1;
        if (isEnterKey(key) && this.selectedIndex != null) {
          this.selectReason(this.selectedIndex);
          return;
        }
        switch (key) {
          case 'ArrowDown':
            if (this.selectedIndex != null) {
              this.selectedIndex = this.selectedIndex === maxReasonIndex ? maxReasonIndex : this.selectedIndex + 1;
            } else {
              this.selectedIndex = 0;
            }
            break;
          case 'ArrowUp':
            if (this.selectedIndex) {
              this.selectedIndex = this.selectedIndex - 1;
            }
            break;
        }
      }
    }
    this.cdr.markForCheck();
  }

  selectFeedback(value: Resources.FeedbackType) {
    this.feedbackSelected = value;

    switch (value) {
      case 'Negative':
        this.feedbackComment = 'Feedback sent';
        this.cdr.markForCheck();
        setTimeout(() => {
          this.displayView = 'wrongReason';
          this.selectedIndex = null;
          this.cdr.markForCheck();
        }, 1000);
        break;
      case 'Positive':
        this.feedbackComment = 'Feedback sent';
        setTimeout(() => {
          this.closePopUp();
        }, 1000);
        break;
      default:
        break;
    }
    this.cdr.markForCheck();
  }

  closePopUp() {
    this.sendFeedback();
    this.unRegisterKeys();
    this.closePopup.emit();
  }

  unRegisterKeys() {
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
  }

  selectReason(index: number) {
    this.reasonSelected = index;
    if (this.WRONG_FEEDBACK_REASONS[index] === 'Other') {
      this.displayView = 'wrongReasonFree';
      this.cdr.markForCheck();
      setTimeout(() => {
        this.textAreaReason?.textArea?.nativeElement.focus();
        this.cdr.markForCheck();
      }, 0);
      return;
    }
    this.finalWrongReason();
  }

  sendFeedback() {
    if (!this.feedbackSelected) return;
    let reason: string;
    if (this.reasonSelected != null) {
      reason = this.freeReason || this.WRONG_FEEDBACK_REASONS[this.reasonSelected];
    }
    this.feedback(reason);
  }

  feedback(reason: string) {
    const request: Resources.FeedbackRequest = {
      timestamp: Date.now(),
      searchId: this.searchId,
      resourcePosition: this.index,
      query: this.query,
      source: 'Web',
      platform: upperFirst(this.appService.platform),
      resourceAppId: this.resourceAppId,
      resourceSnippet: this.snippet,
      type: this.feedbackSelected,
      level: 'Resource',
      linkId: this.linkId,
      resourceId: this.resourceId,
      resourceName: this.resourceName,
      resourceClientLink: this.resourceLinkUrl,
    };
    if (reason) {
      request.reason = reason;
    }
    this.resourcesService.feedback(request);
    this.feedbackSent.emit();
  }

  goBack() {
    this.displayView = 'wrongReason';
    this.cdr.markForCheck();
  }

  onReasonChange($event) {
    this.freeReasonModel = $event;
    this.cdr.markForCheck();
  }

  onSubmitReason() {
    if ((this.freeReasonModel?.trim() || '')?.length < 11) return;
    this.freeReason = this.freeReasonModel;
    this.finalWrongReason();
  }

  private finalWrongReason() {
    this.closePopUp();
  }

  ngOnDestroy(): void {
    this.unRegisterKeys();
  }
}
